import { type IPControllerInfo } from './comm.js';
import type { Device } from '@studio/models/device';
import { generateId } from '@studio/models';
import { type T_Maybe } from '@tryiris-ai/js-types';
import type { Proxy as AgentProxy } from '#~/agent/types.js';

export const CONTROLLER_SCHEME = 'visca-bridge://';

// Saves the array of controllers to the studio state.
export async function saveControllers(processes: IPControllerInfo[], proxy: T_Maybe<AgentProxy>) {
  if (!proxy) {
    console.error('>> No proxy provided for saveControllers');
    return;
  }

  type studioDevice = { [deviceID: string]: { name?: string; control?: { uri?: string } } };

  // Get current controllers from state
  const currentControllers = Object.entries(proxy.state.control?.studio?.device ?? {}).filter(
    ([, device]) => device.control?.uri?.startsWith(CONTROLLER_SCHEME) ?? false
  );

  // Create a map of existing URIs to their device IDs and info
  const existingControllersByUri = new Map<
    string,
    { deviceID: string; name: string | undefined }
  >();
  for (const [deviceID, device] of currentControllers) {
    const uri = device.control?.uri;
    if (uri) {
      existingControllersByUri.set(uri, { deviceID, name: device.name });
    }
  }

  // Create a set of new URIs
  const newUris = new Set(
    processes.map((process) => `${CONTROLLER_SCHEME}${process.localIP}:${process.port}`)
  );

  // Find controllers to add (new URIs not in existing)
  const newDevices: studioDevice = {};
  for (const process of processes) {
    const uri = `${CONTROLLER_SCHEME}${process.localIP}:${process.port}`;
    const existing = existingControllersByUri.get(uri);

    if (!existing) {
      // This is a new controller, create new device
      const deviceID = generateId<Device['id']>();
      newDevices[deviceID] = {
        name: process.name,
        control: { uri }
      };
    } else if (existing.name !== process.name) {
      // Controller exists but name changed, update it
      newDevices[existing.deviceID] = {
        name: process.name,
        control: { uri }
      };
    }
  }

  // Find controllers to remove (existing URIs not in new)
  const oldDevices: studioDevice = {};
  for (const [deviceID, device] of currentControllers) {
    const uri = device.control?.uri;
    if (uri && !newUris.has(uri)) {
      oldDevices[deviceID] = {};
    }
  }

  // Only perform mutations if there are actual changes
  const hasChanges = Object.keys(newDevices).length > 0 || Object.keys(oldDevices).length > 0;

  if (!hasChanges) {
    return;
  }

  // Build mutations
  const addMutation =
    Object.keys(newDevices).length > 0
      ? {
          control: {
            studio: {
              device: newDevices
            }
          }
        }
      : {};

  const removeMutation =
    Object.keys(oldDevices).length > 0
      ? {
          control: {
            studio: {
              device: oldDevices
            }
          }
        }
      : {};

  try {
    await proxy.mutate(addMutation, removeMutation);
  } catch (error) {
    console.error('>> Error saving multiple devices:', error);
  }
}
